import { formForbidden } from "../../api/data";

export enum FetchMethod {
  GET = 'GET',
  POST = 'POST'
}

export type DataAction = 'DataActionUpdate' | 'DataActionAnnotation' | 'DataActionRemark' | 'DataActionAnswer'

export type GlobalValueItem = {
  dataCode: string;
  deCode: string;
  value: string;
  visitName?: string;
}

export type ValueItem = {
  dataCode: string;
  deCode: string;
  inputState?: string; // Runing
  projectCode?: string;
  question?: boolean;
  subjectCode?: string; // GYVR100005
  value: string;
}

export type StackData = {
  [key: string]: unknown;
}

export type Properties = {
  // foo/bar?keyword=${keyword}&name=${name}&id=1900
  server: string;
  method: FetchMethod,
  delay: number;
  labelName: string;
  labelCombine: string | string[];
  query: {
    [key: string]: string | number | boolean;
  },
  payload: {
    [key: string]: string | number | boolean;
  },
  // 需要自动填充的字段
  autofields: (string | [string, string])[]
}

export type FieldConfig = {
  col: string;
  isAdd?: boolean;
  dataCode: string;
  deCode: string;
  label: string;
  type: string;
  sort?: any;
  properties: Properties;
  validationRules: any[];
  logicOperations: any;
  remind: any;
  reminds: any[];
}

export interface AutocompleteItemProps {
  value: string;
  placeholder: string;
  config: FieldConfig;
  isUpdate: boolean;
  values: ValueItem[];
  globals: GlobalValueItem[];
  onChange: (value: string, values?: GlobalValueItem[])=> void;
}

export type SearchResult = {
  code: number;
  message: string;
  success: boolean;
  data: any[]
}

// 解析模板变量 例：/path/to?id=${id}&keyword=${keyword}&title=${title}
const parseTemplateLiterals = (content: string, data: StackData = {}, projectData: StackData = {}) => {
  const re = new RegExp(/(\$\{(\w+)\})/, 'g');

  let defaults: {[k: string]: unknown} = {}
  let match: RegExpExecArray | null;
  while((match = re.exec(content)) !== null) {
    defaults[match[2]] = '';
  }

  const keys = Object.keys({ ...defaults, ...data })
  const values = Object.values({ ...defaults, ...data })
  
  // eslint-disable-next-line no-new-func, prefer-template
  return new Function(...keys, 'project', `
    try {
      return \`${content}\`;
    } catch(e) {
      console.log('template.literals', e)
      // throw new Error('parsed template fail.');
    }
  `)(...values, projectData)
}

// 解析 queryParams,payload 参数值
export const parseParamsValue = (value: string, data: StackData, projectData: StackData={}): string => {
  // ! 表示普通字符串不需要解析
  if(value.startsWith('!')) {
    return value.substring(1)
  }

  let [nameValue, defaultValue] = value.split('|')

  // 从values中查询
  if(nameValue.startsWith('@')) {
    return (projectData[nameValue.substring(1)] as string) ?? (defaultValue ?? '');
  }

  // 从values中查询
  return (data[nameValue] as string) ?? (defaultValue ?? '');
}

// 合并URL参数
export const mergeServerUrl = (props: Properties, keyword: string, data: StackData, projectData: StackData)=> {
  const server = parseTemplateLiterals(props.server, {keyword,  ...data}, projectData)

  // 没有配置queryParams直接跳过
  if(Object.keys(props.query).length === 0) {
    return server
  }

  // 解析地址中的参数
  const canParse = URL.canParse(server)
  const url = new URL(server, canParse ? undefined : window.location.origin)

  // 将参数合并
  for(const name in props.query) {
    let value = props.query[name]
    if(typeof value === 'string') {
      value = parseParamsValue(value, {keyword, ...data}, projectData)
    }
    url.searchParams.set(name, String(value))
  }

  return canParse ? url.toString() : `${url.pathname}${url.search}`
}

// 组合展示的标签
export const parseLabelCombine = (properties: Properties, data: StackData, row: StackData, projectData: StackData)=> {
  const combine = properties.labelCombine

  if(typeof combine === 'string') {
    return parseTemplateLiterals(combine, {...data, ...row}, projectData)
  }

  if(combine.length === 0) {
    combine.push(properties.labelName)
  }

  return combine.map((item)=> parseParamsValue(item, {...data, ...row}, projectData)).join('')
}

// 君实外呼特殊表单
const CALL_CENTER_FORM_CODE = 'FMJSNSCLC01CR03'
// 外呼角色
const CALL_CENTER_ROLE_CODE = 'CALL_CENTER'

/**
 * 判断当前用户是否是只读权限
 * @param formCode 
 * @returns 
 */
export const isReadOnly = (formCode: string): boolean => {
  if (sessionStorage.projectRoleCode === 'ReadOnly') {
    return true
  }
  const roleCode = sessionStorage.roleCode
  if (roleCode === 'EDC_READ') {
    return true
  }
  // 外呼角色
  if (roleCode === CALL_CENTER_ROLE_CODE) {
    // Call Center 以外的表单禁用
    return formCode !== CALL_CENTER_FORM_CODE
  }
  // 其他角色 Call Center 表单禁用
  return formCode === CALL_CENTER_FORM_CODE
}

type Mapping = {
  [key: string]: number
}

// !!!君实 - 表单锁定特殊处理
export class JSFormLock {
  
  // 表单中是否有list类型的字段
  private static hasListField(bodyData: any) {
    for(const t of bodyData) {
        if (t.type === 'list') {
            return true
        }
        if (Array.isArray(t.children)) {
            if (JSFormLock.hasListField(t.children)) {
                return true
            }
        }
    }
    return false
  } // hasListField

  // 将list字段类型从body中挑出来
  private static pickListKeys(listed: boolean, flag: number, body: any) {
    const keys: string[][] = []
    //
    const pick = (listed: boolean, flag: number, body: any)=> {
      body.forEach((t: any)=> {
        if (t.type === 'list') {
          pick(true, flag+1, t.children)
        } else if (Array.isArray(t.children)) {
          pick(listed, flag, t.children)
        } else {
          if (listed) {
            if (Array.isArray(keys[flag])) {
              keys[flag].push(t.dataCode)
            } else {
              keys[flag] = [t.dataCode]
            }
          }
        }
      })
    }

    pick(listed, flag, body)

    return keys
  } // pickListKeys

  // 从已保存的数据中分离出表单最大索引
  private static resolveMaxIndex(values: any) {
    const mapping: {[key: string]: number} = {}
    values.forEach((t: any)=> {
      if (t.dataCode.indexOf('#') > -1) {
        const [dataCode, index] = t.dataCode.split('#')
        if (typeof mapping[dataCode] === 'number') {
          if (mapping[dataCode] < Number(index)) {
            mapping[dataCode] = Number(index)
          }
        } else {
          mapping[dataCode] = Number(index)
        }
      }
    })
    return mapping
  } // resolveMaxIndex

  // 标记list最大索引
  private static resolveValues(keys: string[][], mapping: Mapping) {
    const values: number[] = []
    keys.forEach((a: string[])=> {
      let max = 0
      a.forEach((dataCode: string)=> {
        if (typeof mapping[dataCode] === 'number') {
          if (max < mapping[dataCode]) {
            max = mapping[dataCode]
          }
        }
      })
      values.push(max)
    })
    return values
  } // resolveValues

  // 遍历body 将已提交的字段锁定
  private static takeFields(keys: string[][], values: number[], listed: boolean, body: any) {
    const fields: string[] = []
    
    const take = (listed: boolean, body: any)=> {
      body.forEach((item: any)=> {
        if (Array.isArray(item.children)) {
          take(listed ? listed : item.type === 'list', item.children)
        } else {
          // list字段
          if (listed) {
            // 获得最大
            const maxIndex = keys.findIndex(s=> s.includes(item.dataCode))
            const max = values[maxIndex]
            for(let i=1; i<=max; i++) {
              fields.push(`${item.dataCode}#${i}`)
            }
          } else {
            fields.push(item.dataCode)
          }
        }
      })
    }

    take(false, body)

    return fields
  } // takeFields

  // 移除特定的action
  private static removeDataAction(dataActions: string[], action: string) {
    const i = dataActions.findIndex((a: string)=> a === action)
    if (i > -1) {
      dataActions.splice(i, 1)
    }
  }

  // 君实 - 表单锁定特殊处理
  static async handle(formId: number, data: any) {

    if (!Array.isArray(data.body)) {
      return
    }

    // 表单是否有list类型字段
    const appendAction = this.hasListField(data.body);

    // 移除 DataActionUpdate
    // this.removeDataAction(data.dataActions, 'DataActionUpdate')

    // 表单锁定时使用通用锁表逻辑
    if (data.lockState === 'Complete') {
      // 移除 DataActionRead
      // this.removeDataAction(data.dataActions, 'DataActionRead')

      // 当表单不存在list类型的字段时不需要特别操作使用默认禁用
      if (appendAction) {
        // 挑出所有list中的dataCode
        const keys = this.pickListKeys(false, -1, data.body)

        // 从已提交表单数据中分离出表单最大索引
        const mapping = this.resolveMaxIndex(data.values)

        // 标记list最大索引
        const values = this.resolveValues(keys, mapping)

        // 遍历body 将已提交的字段锁定
        const fields = this.takeFields(keys, values, false, data.body)

        // 表单中没有list类型的字段时全部禁用
        data.body.forEach((t: any)=> {
          const partial = t.ro || {}
          t.ro = {
            ...partial,
            fields: [fields],
            lost_control_add: true
          }
        })
      } else {
        // 表单中没有list类型的字段时全部禁用
        data.body.forEach((t: any)=> {
          if (typeof t.ro === 'undefined') {
            t.ro = true
          }
        })
      }
    }
    // 从工作台入口 - 辅助治疗期和新辅助治疗期特瑞普利单抗给药记录表单
    else if (['FMJSNSCLC01RTR02', 'FMJSNSCLC01RTR04'].includes(data.formCode)) {
      // 动态获取当前表单是否需要禁用
      const r = await formForbidden(formId)
      if (r.code === 0 && r.data.length > 0) {
        data.body.forEach((t: any)=> {
          t.ro = {
            fields: [r.data, []],
            lost_control_add: true,
          }
        })
      }
    }

    // 保留全量提交模式
    if (appendAction || data.lockState === 'Wait') {
      // 强制显示【提交】按钮
      // if (!data.formActions.includes('FormActionSubmit')) {
      //     data.formActions.push('FormActionSubmit')
      // }

      // 强制修改为编辑模式
      if (!data.dataActions.includes('DataActionUpdate') && !data.dataActions.includes('DataActionInput')) {
        data.dataActions.unshift('DataActionUpdate')
      }
    }
  } // handle

  // 君实 - 表单锁定特殊处理
  static async handlefast(formId: number, data: any) {

    if (!Array.isArray(data.body)) {
      return
    }

    // 表单是否有list类型字段
    const appendAction = this.hasListField(data.body);

    // 移除 DataActionUpdate
    this.removeDataAction(data.dataActions, 'DataActionUpdate')

    // 表单锁定时使用通用锁表逻辑
    if (data.lockState === 'Complete') {

      // 当表单不存在list类型的字段时不需要特别操作使用默认禁用
      if (appendAction) {
        
        const fields = data.values.map((t: any)=> t.dataCode)

        // 表单中没有list类型的字段时全部禁用
        data.body.forEach((t: any)=> {
          const partial = t.ro || {}
          t.ro = {
            ...partial,
            fields: [fields],
            lost_control_add: true,
            lost_control_del: true,
          }
        })
      } else {
        // 表单中没有list类型的字段时全部禁用
        data.body.forEach((t: any)=> {
          if (typeof t.ro === 'undefined') {
            t.ro = true
          }
        })
      }
    }
    // 从工作台入口 - 辅助治疗期和新辅助治疗期特瑞普利单抗给药记录表单
    else if (['FMJSNSCLC01RTR02', 'FMJSNSCLC01RTR04'].includes(data.formCode)) {
      // 动态获取当前表单是否需要禁用
      const r = await formForbidden(formId)
      if (r.code === 0 && r.data.length > 0) {
        if (!data.dataActions.includes('DataActionRead')) {
          data.dataActions.unshift('DataActionRead')
        }
        const i = data.dataActions.indexOf('DataActionUpdate')
        if (i > -1) {
          data.dataActions.splice(i, 1)
          data.dataActions.unshift('DataActionUpdate')
        }
        data.body.forEach((t: any)=> {
          t.ro = {
            fields: [r.data, []],
            lost_control_add: true,
            lost_control_del: true,
          }
        })
      }
    }

    // 保留全量提交模式
    if (appendAction || data.lockState === 'Wait') {
      // 强制显示【提交】按钮
      if (!data.formActions.includes('FormActionSubmit')) {
          data.formActions.push('FormActionSubmit')
      }

      // 强制修改为编辑模式
      if (!data.dataActions.includes('DataActionUpdate') && !data.dataActions.includes('DataActionInput')) {
        data.dataActions.unshift('DataActionUpdate')
      }
    }
  } // handlefast

  // 清理所有ro数据中的字段
  static handleclean(fields: string[], body: any) {
    //
    const clear = (fields: string[], target: any)=> {
      target.forEach((t: any)=> {
        if (t.ro && Array.isArray(t.ro.fields)) {

          // 清理
          const ff = t.ro.fields[0] || []
          for(const d of fields) {
            const i = ff.indexOf(d)
            if (i > -1) {
              ff.splice(i, 1)
            }
          }

          if (Array.isArray(t.children)) {
            clear(fields, t.children)
          }
        }
      })
    }

    clear(fields, body)
  }
}


